import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Chip,
  IconButton,
  useTheme,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import BusinessIcon from '@mui/icons-material/Business'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { getLocation, getSalary } from 'pages/Jobs/helpers'
import { PRACTICE_TYPE_ENUM } from 'data/enums'
import PhysicianSignUpDialog from '../Dialog/PhysicianSignUpDialog'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'

export const ListItemJob = ({ data, isActive, onClick, onFavoriteClick }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const location = getLocation(data.city, data.state)
  const salary = getSalary(data.salaryType, data.salaryMin, data.salaryMax)
  const { employerUserId, signedIn } = useSelector(state => state.identity)
  const [openDialog, setOpenDialog] = React.useState(false)

  const onDialogClose = () => {
    setOpenDialog(false)
  }

  const handleDialogOpen = () => {
    setOpenDialog(true)
    dispatch(
      fetchTrackEvent({
        eventName: 'favorite_lead_click',
        eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
        eventData: { jobId: data.id },
      }),
    )
  }

  const hasChips =
    data.specialties?.length > 0 ||
    data.visaSponsorship ||
    data.visas?.length > 0 ||
    data.loanForgiveness ||
    data.pslfCompatible

  return (
    <Card
      onClick={() => onClick(data.id)}
      sx={{
        borderRadius: 1,
        border: isActive
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
        cursor: 'pointer',
        position: 'relative',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderColor: theme.palette.primary.light,
          transition: 'all 0.2s ease-in-out',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
        <Stack spacing={0.75}>
          {/* Row 1: Title + Practice Type + Favorite */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography
              variant='subtitle1'
              sx={{
                color: theme.palette.primary.dark,
                fontWeight: 'medium',
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                lineHeight: 1.3,
                flex: 1,
                minWidth: 0,
              }}
            >
              {data.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexShrink: 0 }}>
              {data.practiceType && PRACTICE_TYPE_ENUM[data.practiceType] && (
                <Chip
                  label={PRACTICE_TYPE_ENUM[data.practiceType]}
                  size='small'
                  variant='outlined'
                  sx={{ height: '20px', fontSize: '0.65rem', display: { xs: 'none', md: 'flex' } }}
                />
              )}
              {!data.deletedAt && !employerUserId && (
                <div onClick={e => e.stopPropagation()}>
                  <IconButton
                    aria-label='favorite'
                    size='small'
                    onClick={e => {
                      e.stopPropagation()
                      signedIn
                        ? onFavoriteClick({
                            jobId: data.id,
                            favorite: data.candidateJob ? !data.candidateJob.favorite : true,
                          })
                        : handleDialogOpen()
                    }}
                    sx={{ p: 0.25 }}
                  >
                    {data.candidateJob && data.candidateJob.favorite ? (
                      <FavoriteIcon color='error' sx={{ fontSize: '1rem' }} />
                    ) : (
                      <FavoriteBorderIcon color='error' sx={{ fontSize: '1rem' }} />
                    )}
                  </IconButton>
                  <PhysicianSignUpDialog
                    openDialog={openDialog}
                    handleDialogClose={onDialogClose}
                    feature='favorite'
                  />
                </div>
              )}
            </Box>
          </Box>

          {/* Row 2: Employer · Location · Salary */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              color: theme.palette.text.secondary,
            }}
          >
            {data.employerName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BusinessIcon sx={{ fontSize: '0.85rem' }} color='action' />
                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                  {data.employerName}
                </Typography>
              </Box>
            )}
            {location && (
              <>
                {data.employerName && (
                  <Typography variant='body2' sx={{ fontSize: '0.7rem', color: theme.palette.grey[400] }}>
                    ·
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: '0.85rem' }} color='action' />
                  <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                    {location}
                  </Typography>
                </Box>
              </>
            )}
            {salary && (
              <>
                <Typography variant='body2' sx={{ fontSize: '0.7rem', color: theme.palette.grey[400] }}>
                  ·
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttachMoneyIcon sx={{ fontSize: '0.85rem' }} color='action' />
                  <Typography variant='body2' sx={{ fontSize: '0.8rem', fontWeight: 'medium' }}>
                    {salary}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Row 3: Specialty chips + benefit chips (desktop only) */}
          {hasChips && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              {data.specialties?.slice(0, 2).map(specialty => (
                <Chip
                  key={specialty?.id}
                  label={specialty?.name}
                  size='small'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              ))}
              {data.specialties?.length > 2 && (
                <Chip
                  label={`+${data.specialties.length - 2}`}
                  size='small'
                  variant='outlined'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              )}
              {(data.visaSponsorship || data.visas?.length > 0) && (
                <Chip
                  label='Visa'
                  size='small'
                  variant='outlined'
                  color='success'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              )}
              {data.loanForgiveness && (
                <Chip
                  label='Loan Forgive.'
                  size='small'
                  variant='outlined'
                  color='success'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              )}
              {data.pslfCompatible && (
                <Chip
                  label='PSLF'
                  size='small'
                  variant='outlined'
                  color='success'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              )}
            </Box>
          )}
        </Stack>
      </CardContent>

      {data.deletedAt && (
        <Typography
          variant='overline'
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            backgroundColor: theme.palette.info.light,
            color: theme.palette.common.white,
            padding: '2px 4px',
            borderRadius: '1px',
          }}
        >
          Archived
        </Typography>
      )}
    </Card>
  )
}
